import { useCallback, useMemo, useState } from 'react'
import type { RoomConnectionActions, UseLocalMediaResult } from '@whereby.com/browser-sdk/react'
import { WherebyToolbarButtonId } from '../../../../../../pure-js/enums/Whereby'
import { WHEREBY_TOOLBAR_BUTTONS } from './toolbarConfig'
import type { ToolbarButtonViewModel } from './ToolbarButton'

const ignoreSettingsClick = () => undefined

const isToolbarItem = (item: ToolbarButtonViewModel | null): item is ToolbarButtonViewModel => item !== null

// Leaving is hidden where the page owns the call; sharing a screen is offered only where the call allows it.
const isToolbarButtonShown = (
  buttonId: WherebyToolbarButtonId,
  shown: { showHangupButton: boolean; canShareScreen: boolean }
): boolean => {
  if (buttonId === WherebyToolbarButtonId.LEAVE) return shown.showHangupButton
  if (buttonId === WherebyToolbarButtonId.SCREENSHARE) return shown.canShareScreen
  return true
}

interface UseWherebyCallControlsParams {
  actions: RoomConnectionActions
  localMedia: UseLocalMediaResult
  isCameraEnabled: boolean
  isMicrophoneEnabled: boolean
  requestLeave: () => void
  showHangupButton: boolean
  canShareScreen: boolean
  isSharingScreen: boolean
  stopCameraCapture: () => void
  translate: (key: string) => string
}

export const useWherebyCallControls = ({
  actions,
  localMedia,
  isCameraEnabled,
  isMicrophoneEnabled,
  requestLeave,
  showHangupButton,
  canShareScreen,
  isSharingScreen,
  stopCameraCapture,
  translate
}: UseWherebyCallControlsParams) => {
  const [showParticipants, setShowParticipants] = useState(false)
  const [deviceMenuAnchorEl, setDeviceMenuAnchorEl] = useState<HTMLElement | null>(null)

  const closeDeviceMenu = useCallback(() => setDeviceMenuAnchorEl(null), [])
  const openDeviceMenu = useCallback((element: HTMLElement) => setDeviceMenuAnchorEl(element), [])

  const selectMicrophone = useCallback(
    (deviceId: string) => {
      localMedia.actions.setMicrophoneDevice(deviceId)
      closeDeviceMenu()
    },
    [closeDeviceMenu, localMedia.actions]
  )

  const selectCamera = useCallback(
    (deviceId: string) => {
      localMedia.actions.setCameraDevice(deviceId)
      closeDeviceMenu()
    },
    [closeDeviceMenu, localMedia.actions]
  )

  const selectSpeaker = useCallback(
    (deviceId: string) => {
      localMedia.actions.setSpeakerDevice(deviceId)
      closeDeviceMenu()
    },
    [closeDeviceMenu, localMedia.actions]
  )

  const toolbarItems = useMemo<ToolbarButtonViewModel[]>(
    () =>
      WHEREBY_TOOLBAR_BUTTONS.map<ToolbarButtonViewModel | null>((config) => {
        if (!isToolbarButtonShown(config.id, { showHangupButton, canShareScreen })) return null
        const baseItem = {
          id: config.id,
          label: translate(config.translationKey),
          Icon: config.icon,
          MutedIcon: config.mutedIcon,
          variant: config.variant
        }

        switch (config.id) {
          case WherebyToolbarButtonId.CAMERA: {
            const shouldEnableCamera = !isCameraEnabled
            return {
              ...baseItem,
              isActive: isCameraEnabled,
              onClick: () => {
                actions.toggleCamera(shouldEnableCamera)
                if (!shouldEnableCamera) stopCameraCapture()
              }
            }
          }
          case WherebyToolbarButtonId.MICROPHONE:
            return {
              ...baseItem,
              isActive: isMicrophoneEnabled,
              onClick: () => actions.toggleMicrophone(!isMicrophoneEnabled)
            }
          case WherebyToolbarButtonId.SCREENSHARE:
            return {
              ...baseItem,
              isActive: isSharingScreen,
              onClick: () => (isSharingScreen ? actions.stopScreenshare() : actions.startScreenshare())
            }
          case WherebyToolbarButtonId.PEOPLE:
            return {
              ...baseItem,
              isActive: showParticipants,
              onClick: () => setShowParticipants((current) => !current)
            }
          case WherebyToolbarButtonId.SETTINGS:
            return { ...baseItem, isActive: Boolean(deviceMenuAnchorEl), onClick: ignoreSettingsClick }
          case WherebyToolbarButtonId.LEAVE:
            return { ...baseItem, onClick: requestLeave }
          default:
            return null
        }
      }).filter(isToolbarItem),
    [
      actions,
      deviceMenuAnchorEl,
      isCameraEnabled,
      isMicrophoneEnabled,
      isSharingScreen,
      canShareScreen,
      requestLeave,
      showHangupButton,
      showParticipants,
      stopCameraCapture,
      translate
    ]
  )

  return {
    closeDeviceMenu,
    deviceMenuAnchorEl,
    openDeviceMenu,
    selectCamera,
    selectMicrophone,
    selectSpeaker,
    showParticipants,
    toolbarItems
  }
}
